import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { extractErrorMessage, extractValidationErrors } from "./errorUtils";

/**
 * Applies an API error to a react-hook-form instance in a consistent,
 * production-ready way:
 *
 *   1. Field-level errors take precedence. Any error the backend ties to a
 *      specific field (schema validation, "email already registered", etc.) is
 *      attached to that field so it renders directly beneath the input. The
 *      first invalid field is focused for keyboard/screen-reader users.
 *   2. Everything else (invalid credentials, expired token, rate limits, server
 *      or network failures) becomes a form-level `root` error. Render it with a
 *      global banner — e.g. `<FormRootError />` — so it reads as a clear alert
 *      rather than a field message.
 *
 * The resolved message is always specific and, where the backend provides one,
 * actionable — never a bare "Validation error" / "Something went wrong".
 *
 * @param error - The error object from the API call
 * @param form  - The react-hook-form instance
 */
export async function handleFormError<T extends FieldValues>(
  error: unknown,
  form: UseFormReturn<T>
): Promise<void> {
  const validationErrors = await extractValidationErrors(error);

  if (validationErrors) {
    const fields = Object.keys(validationErrors) as Path<T>[];
    fields.forEach((field) => {
      form.setError(field, {
        type: "server",
        message: validationErrors[field],
      });
    });

    // Move focus to the first field carrying an error for accessibility.
    if (fields.length > 0) {
      try {
        form.setFocus(fields[0]);
      } catch {
        // The field may not be registered (e.g. mapped from an unknown backend
        // field name); focusing is best-effort and must never throw.
      }
    }
    return;
  }

  // Non-field error → surface as a form-level (global) message.
  const message = await extractErrorMessage(error);
  form.setError("root", {
    type: "server",
    message,
  });
}
